import * as RT from "@radix-ui/react-toast";
import { type ReactNode, useEffect } from "react";
import { create } from "zustand";

type Variant = "info" | "success" | "error" | "warning";

/** Botón dentro del aviso. Lo usa el «Deshacer» del envío de correos. */
export type ToastAction = { label: string; onClick: () => void };

type ToastItem = {
  id: number;
  title: string;
  description?: string;
  variant: Variant;
  duration?: number;
  action?: ToastAction;
};

/** Extras del aviso: cuánto dura y si trae botón. */
export type ToastOpts = { duration?: number; action?: ToastAction };

type State = {
  items: ToastItem[];
  push: (t: Omit<ToastItem, "id">) => void;
  remove: (id: number) => void;
};

let counter = 0;

export const useToastStore = create<State>((set) => ({
  items: [],
  push: (t) =>
    set((s) => ({ items: [...s.items, { ...t, id: ++counter }] })),
  remove: (id) =>
    set((s) => ({ items: s.items.filter((x) => x.id !== id) })),
}));

export function useToast() {
  const push = useToastStore((s) => s.push);
  const con = (variant: Variant) => (title: string, description?: string, opts?: ToastOpts) =>
    push({ title, description, variant, ...opts });
  return {
    info: con("info"),
    success: con("success"),
    error: con("error"),
    warning: con("warning"),
  };
}

const VARIANT_CLASSES: Record<Variant, string> = {
  info: "border-sky-500/30 bg-sky-500/10 text-sky-200",
  success: "border-emerald-500/30 bg-emerald-500/10 text-emerald-200",
  error: "border-red-500/30 bg-red-500/10 text-red-200",
  warning: "border-amber-500/30 bg-amber-500/10 text-amber-200",
};

export function ToastProvider({ children }: { children: ReactNode }) {
  return (
    <RT.Provider swipeDirection="right">
      {children}
      <ToastQueue />
      <RT.Viewport className="fixed bottom-4 right-4 z-[60] flex w-96 max-w-[calc(100vw-2rem)] flex-col gap-2 outline-none" />
    </RT.Provider>
  );
}

function ToastQueue() {
  const items = useToastStore((s) => s.items);
  const remove = useToastStore((s) => s.remove);
  return (
    <>
      {items.map((t) => (
        <ToastEntry key={t.id} item={t} onClose={() => remove(t.id)} />
      ))}
    </>
  );
}

function ToastEntry({ item, onClose }: { item: ToastItem; onClose: () => void }) {
  useEffect(() => {
    const ms = item.duration ?? 5000;
    const id = setTimeout(onClose, ms);
    return () => clearTimeout(id);
  }, [item.duration, onClose]);

  return (
    <RT.Root
      onOpenChange={(open) => !open && onClose()}
      className={`rounded-md border p-3 shadow-lg ${VARIANT_CLASSES[item.variant]}
                  data-[state=open]:animate-in data-[state=closed]:animate-out
                  data-[state=open]:slide-in-from-right
                  data-[state=closed]:slide-out-to-right`}
    >
      <RT.Title className="text-sm font-medium">{item.title}</RT.Title>
      {item.description && (
        <RT.Description className="mt-0.5 text-xs opacity-80">
          {item.description}
        </RT.Description>
      )}
      {item.action && (
        <RT.Action asChild altText={item.action.label}>
          <button
            type="button"
            onClick={() => {
              item.action?.onClick();
              onClose();
            }}
            className="mt-2 rounded border border-current/40 px-2 py-1 text-xs font-medium hover:bg-white/10"
          >
            {item.action.label}
          </button>
        </RT.Action>
      )}
    </RT.Root>
  );
}
